import React, {
  useEffect,
  useState,
  useRef,
  useCallback,
  cloneElement
} from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import { injectTestId } from '@cvent/nucleus-test-automation';
import { Manager, Reference, Popper } from 'react-popper';
import useStyle from './FlyoutStyles';
import EventSetter from './EventSetter';
import usePlacement from './usePlacement';

/**
 * Flyout element for use in Cvent-branded interfaces. Requires a trigger prop
 * to open and close the flyout. The contents of the flyout are supplied to
 * the content prop.
 */

export const Flyout = ({
  accessibilityLabel,
  largeCaret = false,
  defaultOpen = false,
  noPadding = false,
  isRtl = false,
  onClose = () => {},
  onOpen = () => {},
  placement = 'bottom',
  portal,
  preventOverflow = true,
  testID,
  content,
  trigger,
  isFixed = false,
  zIndex,
  ...propDrop
}) => {
  const position = usePlacement(placement, isRtl);
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const triggerRef = useRef(null);

  const handleOpen = useCallback(() => {
    onOpen();
    setIsOpen(true);
  }, [onOpen]);

  const handleClose = useCallback(() => {
    onClose();
    setIsOpen(false);
  }, [onClose]);

  const toggleOpen = () => {
    if (isOpen) {
      handleClose();
    } else {
      handleOpen();
    }
  };

  const accessibilityClick = event => {
    // Open on 'Enter' and 'Space' keypress
    const code = event.charCode || event.keyCode;
    if (code === 32 || code === 13) {
      toggleOpen();
    }
  };

  useEffect(() => {
    if (isOpen) {
      const handleEsc = event => {
        // Close on 'Esc' keypress
        const code = event.charCode || event.keyCode;
        if (code === 27 && isOpen) {
          handleClose();
        }
      };
      window.addEventListener('keydown', handleEsc);

      return () => window.removeEventListener('keydown', handleEsc);
    }
  }, [handleClose, isOpen]);

  const {
    caret,
    caretPosition,
    triggerWrapper,
    flyoutWrapper,
    flyoutPosition
  } = useStyle(zIndex, largeCaret, noPadding, isRtl);

  const renderPopper = () => {
    const portalRef = portal;
    const popperAndContent = (
      <div ref={triggerRef}>
        <Popper
          placement={position}
          positionFixed={isFixed}
          modifiers={{
            preventOverflow: {
              enabled: preventOverflow
            },
            hide: {
              enabled: preventOverflow
            }
          }}
          {...propDrop}
        >
          {({ ref, style, placement: arrowPlacement, arrowProps }) => (
            <div
              ref={ref}
              data-placement={position}
              css={{
                ...flyoutWrapper,
                ...flyoutPosition[
                  arrowPlacement && arrowPlacement.split('-')[0]
                ],
                ...style
              }}
              {...injectTestId(testID)}
            >
              <EventSetter containerRef={triggerRef} onDismiss={handleClose}>
                <div
                  ref={arrowProps.ref}
                  css={{
                    ...arrowProps.style,
                    ...caret,
                    ...caretPosition[
                      arrowPlacement && arrowPlacement.split('-')[0]
                    ]
                  }}
                  aria-label={accessibilityLabel}
                />
                {typeof content === 'function' ? content(handleClose) : content}
              </EventSetter>
            </div>
          )}
        </Popper>
      </div>
    );
    if (portalRef) {
      const portalTarget =
        portalRef === true
          ? typeof window !== 'undefined' && window.document.body
          : portalRef;
      // portal is wrapped in div to suppress prop-type warning from Manager
      return ReactDOM.createPortal(popperAndContent, portalTarget);
    }
    return popperAndContent;
  };

  return (
    <Manager>
      <Reference>
        {({ ref }) => (
          <div ref={ref} css={triggerWrapper}>
            {typeof trigger === 'function'
              ? trigger(() => toggleOpen())
              : cloneElement(trigger, {
                  tabIndex: 0,
                  onClick: toggleOpen,
                  onKeyPress: event => {
                    event.preventDefault();
                    accessibilityClick(event);
                  }
                })}
          </div>
        )}
      </Reference>
      {isOpen && renderPopper()}
    </Manager>
  );
};

Flyout.propTypes = {
  /** Element content displayed within the flyout
   * For basic use-cases, pass in your html element or component directly
   * If you need to control when the flyout closes, use a function with render props
   * Example:
   * content={closeFlyout => <button onClick={closeFlyout}>Click to Close</button>}
   */
  content: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.func
  ]),
  /** Content that triggers the flyout
   * For basic use-cases, you can pass in your html element or component directly
   * If you need more control over open/close events, use a function with render props
   * Example:
   *
   * const [disabled, setDisabled] = useState(false);
   *
   * ...code...
   *
   * trigger={handleClick => (
   *  <button onClick={() => {
   *     if !disabled handleClick();
   *   }}>
   *    Open
   *  </button>
   * )}
   */
  trigger: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.func
  ]).isRequired,
  /** Screen reader label text (e.g. aria-label) */
  accessibilityLabel: PropTypes.string,
  /** defaultOpen will set the popover to open upon component mount */
  defaultOpen: PropTypes.bool,
  /** isFixed will switch popover positioning from absolute to fixed.
   *  If you''re seeing the popover affect surrounding containers or cause overflow problems,
   *  use this setting to possibly resolve.
   */
  isFixed: PropTypes.bool,
  /** Text direction */
  isRtl: PropTypes.bool,
  /** Bool to set caret size */
  largeCaret: PropTypes.bool,
  /** Call a function upon the Popover closing. No return value. */
  onClose: PropTypes.func,
  /** Call a function upon the Popover opening. No return value. */
  onOpen: PropTypes.func,
  /** Removes default padding from flyout */
  noPadding: PropTypes.bool,
  /** Initial placement for the flyout. Default is bottom. */
  placement: PropTypes.oneOf([
    'auto',
    'top',
    'top-start',
    'top-end',
    'end',
    'end-top',
    'end-bottom',
    'bottom',
    'bottom-start',
    'bottom-end',
    'start',
    'start-top',
    'start-bottom'
  ]),
  /** DOM node reference to render menu inside. If boolean 'true' is provided, Menu will be appended to the 'body' element. */
  portal: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  /** Set preventOverflow to true if you wish the Popover to flow outside the container */
  preventOverflow: PropTypes.bool,
  /** For e2e tests (maps to `data-cvent-id`) */
  testID: PropTypes.string,
  /** zIndex allows the user to alter Flyout's default z-index */
  zIndex: PropTypes.number
};
